import {
  Injectable,
  computed,
  inject,
  signal,
  type Signal,
  type WritableSignal,
} from '@angular/core';
import { SwUpdate, type VersionEvent } from '@angular/service-worker';

export type CacheStatus = 'DISABLED' | 'READY' | 'UPDATING' | 'UPDATE_AVAILABLE' | 'ERROR';

@Injectable({ providedIn: 'root' })
export class PwaStatusService {
  private readonly swUpdate: SwUpdate = inject(SwUpdate);

  private readonly onlineSignal: WritableSignal<boolean> = signal<boolean>(this.detectOnlineStatus());
  private readonly cacheStatusSignal: WritableSignal<CacheStatus> = signal<CacheStatus>(
    this.swUpdate.isEnabled ? 'READY' : 'DISABLED',
  );
  private readonly statusTextSignal: WritableSignal<string> = signal<string>(
    this.swUpdate.isEnabled ? 'Cache gotowy' : 'Service Worker wyłączony w tym trybie',
  );

  public readonly isOnline: Signal<boolean> = computed<boolean>(() => this.onlineSignal());
  public readonly cacheStatus: Signal<CacheStatus> = computed<CacheStatus>(() => this.cacheStatusSignal());
  public readonly statusText: Signal<string> = computed<string>(() => this.statusTextSignal());

  public constructor() {
    this.bindNetworkEvents();
    this.bindServiceWorkerEvents();
  }

  public async checkForUpdates(): Promise<void> {
    if (!this.swUpdate.isEnabled) {
      return;
    }

    try {
      await this.swUpdate.checkForUpdate();
    } catch {
      this.cacheStatusSignal.set('ERROR');
      this.statusTextSignal.set('Nie udało się sprawdzić aktualizacji cache.');
    }
  }

  public reloadForUpdate(): void {
    if (typeof window === 'undefined') {
      return;
    }

    window.location.reload();
  }

  private bindNetworkEvents(): void {
    if (typeof window === 'undefined') {
      return;
    }

    window.addEventListener('online', (): void => {
      this.onlineSignal.set(true);
    });

    window.addEventListener('offline', (): void => {
      this.onlineSignal.set(false);
    });
  }

  private bindServiceWorkerEvents(): void {
    if (!this.swUpdate.isEnabled) {
      return;
    }

    this.swUpdate.versionUpdates.subscribe((event: VersionEvent): void => {
      if (event.type === 'VERSION_DETECTED') {
        this.cacheStatusSignal.set('UPDATING');
        this.statusTextSignal.set('Aktualizacja cache w toku...');
        return;
      }

      if (event.type === 'VERSION_READY') {
        this.cacheStatusSignal.set('UPDATE_AVAILABLE');
        this.statusTextSignal.set('Nowa wersja aplikacji jest gotowa.');
        return;
      }

      if (event.type === 'VERSION_INSTALLATION_FAILED') {
        this.cacheStatusSignal.set('ERROR');
        this.statusTextSignal.set('Nie udało się zainstalować aktualizacji cache.');
        return;
      }

      if (event.type === 'NO_NEW_VERSION_DETECTED') {
        this.cacheStatusSignal.set('READY');
        this.statusTextSignal.set('Cache gotowy');
      }
    });
  }

  private detectOnlineStatus(): boolean {
    if (typeof navigator === 'undefined') {
      return true;
    }

    return navigator.onLine;
  }
}
